// apps/web/src/features/clients/components/ClientsTable.tsx
//
// Desktop rows + purpose-built mobile cards for the `/clients` directory.
// Presentation only: `rows` arrive from the page-level cursor query, so this
// file renders without a QueryClientProvider.
//
// INVARIANT (AC-2): DataTable's built-in client paging and client sorting are
// disabled here. The server owns row order (`updatedAt DESC, id DESC`) and the
// page owns "load more", so a second, local pager would hide loaded records
// behind a page number and a local A→Z sort would imply a global ordering the
// backend never produced.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientListItem } from '@magimp/shared';
import { DataTable, RowCard, type Column } from '../../../ui/index.js';
import {
  ClientContactCell,
  ClientNameCell,
  ClientQuickActions,
  displayPhone,
} from './clientRowParts.js';

export interface ClientsTableProps {
  rows: readonly ClientListItem[];
  onSelect: (c: ClientListItem) => void;
  onCopyPhone: (c: ClientListItem) => void;
  /** Filter signature the return anchor is recorded under. */
  filterKey: string;
  emptyState?: ReactNode;
  /** When true, render the manager column. Admin scope only. */
  showManager?: boolean;
}

export function ClientsTable({
  rows,
  onSelect,
  onCopyPhone,
  filterKey,
  emptyState,
  showManager,
}: ClientsTableProps) {
  const { t } = useTranslation(['clients', 'common']);

  const columns: Column<ClientListItem>[] = [
    {
      key: 'name',
      label: t('clients:fields.name'),
      render: (c) => <ClientNameCell client={c} filterKey={filterKey} />,
    },
    {
      key: 'primaryContact',
      label: t('clients:fields.primaryContact'),
      render: (c) => <ClientContactCell client={c} />,
    },
    {
      // Empty label marks this an action cell: DataTable stops click/keydown
      // from reaching the row, so calling or copying never opens the client.
      key: 'contactActions',
      label: '',
      width: 88,
      render: (c) => <ClientQuickActions client={c} onCopyPhone={onCopyPhone} />,
    },
    {
      key: 'legalEntitiesCount',
      label: t('clients:fields.legalEntities'),
      align: 'right',
      width: 96,
      render: (c) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c.legalEntitiesCount}</span>
      ),
    },
  ];
  if (showManager) {
    columns.push({
      key: 'manager',
      label: t('clients:fields.manager'),
      width: 160,
      render: (c) => c.manager.name,
    });
  }

  return (
    <DataTable<ClientListItem>
      columns={columns}
      rows={rows}
      onSelect={onSelect}
      emptyState={emptyState}
      getRowKey={(c) => c.id}
      // One paging model: everything loaded is on one page (see file header).
      pageSize={Math.max(rows.length, 1)}
      renderMobileCard={({ row }) => (
        <ClientMobileCard
          client={row}
          filterKey={filterKey}
          showManager={!!showManager}
          onSelect={() => onSelect(row)}
          onCopyPhone={onCopyPhone}
        />
      )}
    />
  );
}

interface ClientMobileCardProps {
  client: ClientListItem;
  filterKey: string;
  showManager: boolean;
  onSelect: () => void;
  onCopyPhone: (c: ClientListItem) => void;
}

/**
 * Mobile card for one client. Name and contact lead; the count/manager
 * metadata sits underneath as a compact caption pair — the generic stacked
 * DataTable card would give the action column and the counts the same weight
 * as the client itself.
 */
function ClientMobileCard({
  client,
  filterKey,
  showManager,
  onSelect,
  onCopyPhone,
}: ClientMobileCardProps) {
  const { t } = useTranslation(['clients']);
  const contact = client.primaryContact;
  const fields = [
    {
      label: t('clients:fields.primaryContact'),
      value: contact ? (
        <span style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ overflowWrap: 'anywhere' }}>{contact.fullName}</span>
          <span style={{ color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>
            {displayPhone(contact.phone)}
          </span>
        </span>
      ) : (
        t('clients:noPrimaryContact')
      ),
    },
    {
      label: t('clients:fields.legalEntities'),
      value: (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{client.legalEntitiesCount}</span>
      ),
    },
  ];
  if (showManager) {
    fields.push({ label: t('clients:fields.manager'), value: client.manager.name });
  }

  return (
    <RowCard
      ariaLabel={client.name}
      archived={!!client.archivedAt}
      onSelect={onSelect}
      header={<ClientNameCell client={client} filterKey={filterKey} />}
      actions={<ClientQuickActions client={client} onCopyPhone={onCopyPhone} touch />}
      fields={fields}
    />
  );
}
